import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import marubitLogo from "../../assets/images/marubit_logo.png";
import { NavItem } from "../../store/navStore";
import { getApiUrl } from "../../api";
import { useAuthStore } from "../../store/authStore";

interface MobileSidebarProps {
  open: boolean;
  onClose: () => void;
  openMenu: string | null;
  setOpenMenu: React.Dispatch<React.SetStateAction<string | null>>;
  navItems: NavItem[];
}

export default function MobileSidebar({ open, onClose, openMenu, setOpenMenu, navItems }: MobileSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, role, clearAuth } = useAuthStore();
  const currentPath = location.pathname;

  const toggleMenu = (label: string) => {
    setOpenMenu(prev => prev === label ? null : label);
  };

  const handleLogout = async () => {
    const token = localStorage.getItem("access_token");
    try {
      await fetch(getApiUrl("/marubit/api/member/logout"), {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
    clearAuth();
    onClose();
    navigate("/");
  };

  return (
    <>
      {/* 배경 오버레이 */}
      {open && (
        <div
          onClick={onClose}
          className="fixed inset-0 bg-black/30 z-[200]"
        />
      )}

      {/* 사이드바 */}
      <div className={[
        "fixed top-0 left-0 w-[300px] h-full bg-white z-[201] flex flex-col overflow-y-auto shadow-[4px_0_20px_rgba(0,0,0,0.15)]",
        open ? "flex" : "hidden",
      ].join(" ")}>

        {/* 헤더 */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <Link to="/" onClick={onClose} className="no-underline">
            <img src={marubitLogo} alt="마루빛" className="h-12 w-auto" />
          </Link>
          <button
            onClick={onClose}
            className="bg-transparent border-none cursor-pointer p-1 text-gray-500"
          >
            <svg viewBox="0 0 24 24" width={24} height={24} fill="none" stroke="currentColor" strokeWidth={2}>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* 메뉴 */}
        <nav className="flex-1">
          <ul className="list-none m-0 p-0">
            {navItems.map((item) => {
              const hasChildren = !!item.children?.length;
              const isOpen = openMenu === item.label;
              const isActive = item.href
                ? currentPath === item.href
                : item.children?.some(c => c.href && currentPath === c.href);

              return (
                <li key={item.label} className="border-b border-[#ebebeb]">
                  {hasChildren ? (
                    <>
                      <button
                        onClick={() => toggleMenu(item.label)}
                        className={[
                          "w-full flex items-center justify-between px-5 py-4 border-none cursor-pointer text-[16px] text-left transition-colors duration-150",
                          isOpen
                            ? "bg-[#fdf8f0] font-semibold text-[#887267]"
                            : "bg-transparent font-medium text-[#222]",
                        ].join(" ")}
                      >
                        {item.label}
                        <svg
                          viewBox="0 0 24 24" width={16} height={16}
                          fill="none" stroke="currentColor" strokeWidth={2.5}
                          className={[
                            "transition-transform duration-200",
                            isOpen ? "rotate-180 text-[#887267]" : "rotate-0 text-[#aaa]",
                          ].join(" ")}
                        >
                          <polyline points="6 9 12 15 18 9" />
                        </svg>
                      </button>

                      {isOpen && (
                        <ul className="list-none m-0 py-[4px_0_12px_0] bg-white pl-10">
                          {item.children!.map((sub, idx) => {
                            const subActive = currentPath === sub.href;
                            const isLast = idx === item.children!.length - 1;
                            return (
                              <li key={sub.label} className="relative pl-10">
                                <span className={[
                                  "absolute left-6 top-0 w-px bg-[#ddd]",
                                  isLast ? "bottom-1/2" : "bottom-0",
                                ].join(" ")} />
                                <span className={[
                                  "absolute left-5 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full z-[1]",
                                  subActive ? "bg-[#887267]" : "bg-[#ddd]",
                                ].join(" ")} />
                                <Link
                                  to={sub.href ?? "/"}
                                  onClick={onClose}
                                  className={[
                                    "block py-[10px] pr-5 pl-4 text-[14px] no-underline",
                                    subActive ? "text-[#887267] font-semibold" : "text-[#555] font-normal",
                                  ].join(" ")}
                                >
                                  {sub.label}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </>
                  ) : (
                    <Link
                      to={item.href ?? "/"}
                      onClick={onClose}
                      className={[
                        "block px-5 py-4 text-[16px] no-underline transition-colors duration-150",
                        isActive
                          ? "font-bold text-[#887267] bg-[#fdf8f0]"
                          : "font-medium text-[#222] bg-transparent",
                      ].join(" ")}
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* 하단 */}
        <div className="border-t border-gray-200 px-5 py-4 flex flex-col gap-3">
          {isLoggedIn ? (
            <>
              <Link
                to={role === 1 ? "/admin" : "/mypage"}
                onClick={() => { sessionStorage.removeItem("mypage_verified"); onClose(); }}
                className="flex items-center gap-2 text-[#333] no-underline text-[15px]"
              >
                <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {role === 1 ? "관리자페이지" : "마이페이지"}
              </Link>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 text-red-400 bg-transparent border-none cursor-pointer text-[15px] p-0"
              >
                <svg viewBox="0 0 24 24" width={18} height={18} fill="none" stroke="currentColor" strokeWidth={2}>
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
                로그아웃
              </button>
            </>
          ) : (
            <>
              <Link
                to="/auth/login"
                onClick={onClose}
                className="flex items-center gap-2 text-[#887267] no-underline text-[15px] font-semibold"
              >
                로그인
              </Link>
              <Link
                to="/auth/register"
                onClick={onClose}
                className="flex items-center gap-2 text-[#333] no-underline text-[15px]"
              >
                회원가입
              </Link>
            </>
          )}
        </div>
      </div>
    </>
  );
}